import {
  faAward,
  faChartPie,
  faDollarSign,
  faFlask,
  faGraduationCap,
  faHospital,
  faPeopleGroup,
  faSchool,
  faUserGroup,
} from '@fortawesome/pro-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { ProviderNetworkEdgeSubtype } from 'src/api/redux';

/** The connection types the carousel renders — one legend key and pill style each. */
export type SimilarProviderConnectionType =
  | 'patient_sharing'
  | 'same_group'
  | 'same_site_of_care'
  | 'trained_with'
  | 'alumni'
  | 'same_society'
  | 'shared_manufacturer_payments'
  | 'shared_research_study'
  | 'shared_company_ownership';

interface ConnectionMeta {
  /** Legend text for this connection type, and the pill text when the edge names nothing */
  label: string;
  /** Legend tooltip explaining what the connection means */
  description: string;
  icon: IconDefinition;
  /** Pill background + text, used on the card's connection chips */
  pillClassName: string;
  /** Solid swatch, used for the card's accent bar */
  accentClassName: string;
}

/**
 * Maps each API `edge_subtype` onto the connection type it renders as. A full `Record`, so
 * adding a subtype to `ProviderNetworkEdgeSubtype` without mapping it fails the type
 * check. Subtypes the API sends that the frontend doesn't know yet are still dropped at
 * runtime (see `connectionTypeFor`).
 *
 * `IN_TRAINING` renders as "Trained With" — the carousel doesn't distinguish training
 * together now from having trained together.
 *
 * `SHARED_PROGRAM` is the data asset's name for providers paid by the same manufacturer
 * for the same type of work, so it renders as "Shared Manufacturer Payments".
 */
export const EDGE_SUBTYPE_CONNECTION_TYPE: Record<
  ProviderNetworkEdgeSubtype,
  SimilarProviderConnectionType
> = {
  PATIENT_SHARING: 'patient_sharing',
  SAME_GROUP: 'same_group',
  SAME_SITE_OF_CARE: 'same_site_of_care',
  TRAINED_WITH: 'trained_with',
  IN_TRAINING: 'trained_with',
  ALUMNI: 'alumni',
  SAME_SOCIETY: 'same_society',
  SHARED_PROGRAM: 'shared_manufacturer_payments',
  CO_INVESTIGATOR: 'shared_research_study',
  CO_OWNER: 'shared_company_ownership',
};

/** Training edges are the only ones carrying real years, so only they yield a class year. */
export const TRAINING_EDGE_SUBTYPES: ProviderNetworkEdgeSubtype[] = [
  'TRAINED_WITH',
  'ALUMNI',
  'IN_TRAINING',
];

/**
 * Which connection drives the card's accent bar, strongest first. Patient sharing
 * outranks the rest because it is the only quantified connection.
 */
export const CONNECTION_TYPE_PRECEDENCE: SimilarProviderConnectionType[] = [
  'patient_sharing',
  'same_group',
  'same_site_of_care',
  'trained_with',
  'alumni',
  'same_society',
  'shared_manufacturer_payments',
  'shared_research_study',
  'shared_company_ownership',
];

/**
 * Visual treatment per connection type. Kept as static Tailwind class strings
 * so the palette is picked by key rather than composed at runtime — dynamic
 * class names would be dropped by Tailwind's content scan.
 *
 * Every type gets its own hue so a pill's color alone identifies it: sky, teal, lime,
 * purple, indigo, amber, magenta, orange, rose. Nine is about as many as stay
 * distinguishable at pill size, so the closest neighbours are split across unrelated
 * types (and the icon still differs). Orange uses its 900 text shade because the custom
 * orange-700 is too light to read on its own tint, and its 100 tint so the background
 * separates from amber's. Red is avoided because it reads as an error, and neutral is the
 * unrecognised-subtype fallback below.
 */
export const CONNECTION_META: Record<
  SimilarProviderConnectionType,
  ConnectionMeta
> = {
  patient_sharing: {
    label: 'Patient Sharing',
    description: 'Providers who care for the same patients',
    icon: faUserGroup,
    pillClassName: 'bg-sky-50 text-sky-700',
    accentClassName: 'bg-sky-600',
  },
  same_group: {
    label: 'Same Group',
    description: 'Providers who practice in the same group',
    icon: faPeopleGroup,
    pillClassName: 'bg-teal-050 text-teal-700',
    accentClassName: 'bg-teal-500',
  },
  same_site_of_care: {
    label: 'Same Site of Care',
    description: 'Providers who work at the same office, clinic or hospital',
    icon: faHospital,
    pillClassName: 'bg-lime-50 text-lime-700',
    accentClassName: 'bg-lime-500',
  },
  trained_with: {
    label: 'Trained With',
    description:
      'Providers who trained in the same residency, fellowship or internship program around the same time',
    icon: faGraduationCap,
    pillClassName: 'bg-purple-050 text-purple-700',
    accentClassName: 'bg-purple-500',
  },
  alumni: {
    label: 'Alumni',
    description:
      'Providers who went to the same medical school and graduated within 3 years of each other',
    icon: faSchool,
    pillClassName: 'bg-indigo-050 text-indigo-700',
    accentClassName: 'bg-indigo-500',
  },
  same_society: {
    label: 'Same Society',
    description: 'Providers who are members of the same professional society',
    icon: faAward,
    pillClassName: 'bg-amber-100 text-amber-700',
    accentClassName: 'bg-amber-500',
  },
  shared_manufacturer_payments: {
    label: 'Shared Manufacturer Payments',
    description:
      'Providers who were paid by the same manufacturer for the same type of work',
    icon: faDollarSign,
    pillClassName: 'bg-magenta-050 text-magenta-700',
    accentClassName: 'bg-magenta-500',
  },
  shared_research_study: {
    label: 'Shared Research Study',
    description:
      'Providers who were investigators on the same industry-funded research study',
    icon: faFlask,
    pillClassName: 'bg-orange-100 text-orange-900',
    accentClassName: 'bg-orange-500',
  },
  shared_company_ownership: {
    label: 'Shared Company Ownership',
    description:
      'Providers who hold or held an ownership stake in the same company',
    icon: faChartPie,
    pillClassName: 'bg-rose-50 text-rose-700',
    accentClassName: 'bg-rose-500',
  },
};

/** Legend order — strongest connection first, matching the accent precedence */
export const LEGEND_CONNECTION_TYPES: SimilarProviderConnectionType[] =
  CONNECTION_TYPE_PRECEDENCE;

/**
 * Avatar backgrounds, picked deterministically per provider so a given
 * provider keeps the same color across renders.
 */
export const AVATAR_CLASSES = [
  'bg-indigo-400',
  'bg-magenta-400',
  'bg-orange-500',
  'bg-purple-400',
  'bg-teal-400',
  'bg-red-400',
  'bg-sky-500',
];

/** Fallback shown when a connection type is missing from CONNECTION_META */
export const FALLBACK_CONNECTION_META: ConnectionMeta = {
  label: 'Related provider',
  description: 'Providers connected to this provider',
  icon: faUserGroup,
  pillClassName: 'bg-neutral-100 text-neutral-700',
  accentClassName: 'bg-neutral-400',
};

/** Headline metric copy */
export const SHARED_PATIENTS_LABEL = 'shared patients';
export const SHARED_ATTRIBUTES_LABEL = 'shared attributes';

/**
 * Reason copy, in the order they are checked. Every one of these names a fact the
 * response actually carries — there is deliberately no generic last resort, because a
 * card the API ranked in without a nameable connection gives us nothing to claim.
 */
export const REASON_TOP_REFERRER = 'top referring provider';
export const REASON_SAME_FACILITY = 'same primary facility';
export const REASON_SAME_SPECIALTY = 'same specialty';
export const REASON_SAME_TRAINING = 'same residency class';

/** One `scrollBy` step on the carousel — roughly two cards (w-72 + gap-4) */
export const CAROUSEL_SCROLL_AMOUNT = 608;

/**
 * Cards requested per profile. The API clamps to 50, but the carousel is a browse
 * surface rather than a list — 20 is well past what anyone scrolls, and keeps the
 * payload (and its per-card tag/CRM/list hydration) smaller.
 */
export const PROVIDER_NETWORK_PAGE_SIZE = 20;

/**
 * What "Load more" asks for. This is the API's own ceiling — it clamps `pageSize` to 50,
 * and the source caps a profile's network at ~50 related providers — so one click loads
 * everything there is and the button retires. There is no cursor to page with: the
 * endpoint has no pagination envelope, it just returns the top `pageSize` by rank.
 */
export const PROVIDER_NETWORK_MAX_PAGE_SIZE = 50;
